import { useEffect, useRef, useState } from 'react';
import type maplibregl from 'maplibre-gl';
import { TAP, useTheme } from '../theme';
import { PITCH_MAX, PITCH_TILTED, pitchForKey, pitchFromTrack } from './mapGeometry';

/** Tummens storlek; skenan börjar och slutar en halv tumme in så att tummen inte sticker ut. */
const THUMB = 20;

/**
 * Reglage för 3D-kartans lutning (0–80°) i vänstermenyn: Platt · vågrät skena · Luta (K7).
 * Eget reglage i stället för <input type="range">: range-fält beter sig olika mellan
 * WebKit-versioner i iPhone-appen. Följer kartan även när lutningen ändras med två fingrar.
 */
export function PitchControl({ map }: { map: maplibregl.Map }) {
  const T = useTheme();
  const [pitch, setPitch] = useState(() => Math.round(map.getPitch()));
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  // Högst en React-uppdatering per bildruta medan kartan lutar.
  useEffect(() => {
    let raf = 0;
    const onPitch = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; setPitch(Math.round(map.getPitch())); });
    };
    map.on('pitch', onPitch);
    return () => { map.off('pitch', onPitch); cancelAnimationFrame(raf); };
  }, [map]);

  const apply = (p: number, animate: boolean) => {
    if (animate) map.easeTo({ pitch: p, duration: 500 });
    else map.jumpTo({ pitch: p });
    setPitch(p);
  };
  // Vågrät skena: vänster = platt, höger = lutad. Speglas till pitchFromTrack (lodrät, nederst = platt).
  const fromPointer = (clientX: number) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = r.left + THUMB / 2, width = r.width - THUMB;
    apply(pitchFromTrack(2 * left + width - clientX, left, width), false);
  };

  const frac = pitch / PITCH_MAX;
  const chip = (on: boolean): React.CSSProperties => ({
    height: 32, padding: '0 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: on ? 600 : 400,
    border: `1.5px solid ${on ? T.amber : T.border}`, background: on ? `color-mix(in srgb, ${T.amber} 14%, transparent)` : 'transparent',
    color: on ? T.accentText : T.textSub, flexShrink: 0,
  });

  return (
    <div role="group" aria-label="Kartans lutning" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0 4px' }}>
      <button type="button" style={chip(pitch <= 2)} title="Platt karta" onClick={() => apply(0, true)}>Platt</button>
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label="Lutning"
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={PITCH_MAX}
        aria-valuenow={pitch}
        aria-valuetext={`${pitch} grader`}
        onPointerDown={e => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); fromPointer(e.clientX); }}
        onPointerMove={e => { if (dragging.current) fromPointer(e.clientX); }}
        onPointerUp={() => { dragging.current = false; }}
        onPointerCancel={() => { dragging.current = false; }}
        onKeyDown={e => {
          const next = pitchForKey(pitch, e.key);
          if (next == null) return;
          e.preventDefault();
          apply(next, false);
        }}
        style={{ position: 'relative', flex: 1, height: TAP, touchAction: 'none', cursor: 'ew-resize', outlineOffset: -2 }}
      >
        <div style={{ position: 'absolute', top: '50%', left: THUMB / 2, right: THUMB / 2, height: 2, marginTop: -1, borderRadius: 1, background: T.borderHover }} />
        <div style={{ position: 'absolute', top: '50%', left: THUMB / 2, width: `calc((100% - ${THUMB}px) * ${frac})`, height: 2, marginTop: -1, borderRadius: 1, background: T.amber }} />
        <div style={{
          position: 'absolute', top: '50%', left: `calc((100% - ${THUMB}px) * ${frac})`, width: THUMB, height: THUMB, marginTop: -THUMB / 2,
          borderRadius: '50%', background: T.bg, border: `2px solid ${T.amber}`, boxSizing: 'border-box', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        }} />
      </div>
      <button type="button" style={chip(pitch >= PITCH_TILTED - 2)} title="Luta kartan" onClick={() => apply(PITCH_TILTED, true)}>Luta</button>
      <span style={{ color: T.textSub, fontSize: 12, width: 30, textAlign: 'right', flexShrink: 0 }}>{pitch}°</span>
    </div>
  );
}
